import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  DollarSign,
  Plus,
  Filter,
  ChevronRight,
  Check,
  X,
  HelpCircle,
  Lock,
  Globe,
  Camera,
  MessageCircle,
  Share2,
  MoreHorizontal,
  Edit,
  Trash2
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { EventMemberPicker } from './events/EventMemberPicker';
import { EventShareDialog } from './events/EventShareDialog';
import { eventDateValue, eventIsPast } from './events/eventSharing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Event type options
const eventTypes = [
  { value: 'casual', label: 'Casual Round', icon: '🏌️' },
  { value: 'tournament', label: 'Tournament', icon: '🏆' },
  { value: 'outing', label: 'Golf Outing', icon: '👥' },
  { value: 'lesson', label: 'Lesson/Clinic', icon: '📚' },
  { value: 'tee_time', label: 'Tee Time', icon: '📅' },
  { value: 'other', label: 'Other', icon: '⛳' },
];

// Filter tabs
const filterTabs = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'my_events', label: 'My Events' },
  { id: 'past', label: 'Past' },
];

// Event Card Component
const EventCard = ({ event, onRSVP, onView, currentUserId }) => {
  const isOrganizer = event.organizer_id === currentUserId;
  const goingCount = event.participant_count || 0;
  const eventDate = eventDateValue(event.event_date);
  const isPast = eventIsPast(event);
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'going': return 'text-green-500 bg-green-500/10';
      case 'maybe': return 'text-yellow-500 bg-yellow-500/10';
      case 'not_going': return 'text-red-500 bg-red-500/10';
      case 'invited': return 'text-blue-500 bg-blue-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getTypeIcon = (type) => {
    const t = eventTypes.find(e => e.value === type);
    return t?.icon || '⛳';
  };

  return (
    <div 
      className="dark-card p-4 cursor-pointer hover:border-[var(--ps-border-light)] transition-colors"
      onClick={() => onView(event.event_id)}
      data-testid={`event-card-${event.event_id}`}
    >
      {/* Cover Image */}
      {event.cover_image && (
        <div className="relative -mx-4 -mt-4 mb-4 h-32 overflow-hidden rounded-t-xl">
          <img 
            src={event.cover_image} 
            alt={event.title} 
            className="w-full h-full object-cover"
          />
          {event.is_private && (
            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Private
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
            event.event_type === 'tee_time' 
              ? 'bg-amber-500/20 border border-amber-500/30' 
              : 'bg-[var(--ps-primary-light)]'
          }`}>
            {getTypeIcon(event.event_type)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-[var(--ps-text-primary)] line-clamp-1">{event.title}</h3>
              {event.event_type === 'tee_time' && (
                <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-500 rounded font-medium">
                  TEE TIME
                </span>
              )}
            </div>
            <p className="text-sm text-[var(--ps-text-muted)]">
              by {event.organizer_name}
              {isOrganizer && <span className="text-[var(--ps-primary)] ml-1">(You)</span>}
            </p>
          </div>
        </div>
        {event.user_rsvp && (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(event.user_rsvp)}`}>
            {event.user_rsvp === 'going' ? 'Going' : 
             event.user_rsvp === 'maybe' ? 'Maybe' :
             event.user_rsvp === 'invited' ? 'Invited' : 'Not Going'}
          </span>
        )}
      </div>

      {/* Details */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-[var(--ps-text-secondary)]">
          <Calendar className="w-4 h-4 text-[var(--ps-primary)]" />
          <span>{eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
          {event.start_time && (
            <>
              <Clock className="w-4 h-4 text-[var(--ps-primary)] ml-2" />
              <span>{event.start_time}</span>
            </>
          )}
        </div>
        {(event.course_name || event.location) && (
          <div className="flex items-center gap-2 text-sm text-[var(--ps-text-secondary)]">
            <MapPin className="w-4 h-4 text-[var(--ps-primary)]" />
            <span className="line-clamp-1">{event.course_name || event.location}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-[var(--ps-text-secondary)]">
          <Users className="w-4 h-4 text-[var(--ps-primary)]" />
          <span>{goingCount} going</span>
          {event.max_participants && (
            <span className="text-[var(--ps-text-muted)]">/ {event.max_participants} max</span>
          )}
        </div>
        {event.entry_fee > 0 && (
          <div className="flex items-center gap-2 text-sm text-[var(--ps-text-secondary)]">
            <DollarSign className="w-4 h-4 text-[var(--ps-primary)]" />
            <span>${event.entry_fee} entry fee</span>
          </div>
        )}
      </div>

      {/* RSVP Buttons */}
      {!isPast && !isOrganizer && (
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            className={`flex-1 ${event.user_rsvp === 'going' ? 'dark-btn-primary' : 'dark-btn-secondary'}`}
            onClick={() => onRSVP(event.event_id, 'going')}
          >
            <Check className="w-4 h-4 mr-1" />
            Going
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className={`${event.user_rsvp === 'maybe' ? 'text-yellow-500' : 'text-[var(--ps-text-secondary)]'}`}
            onClick={() => onRSVP(event.event_id, 'maybe')}
          >
            <HelpCircle className="w-4 h-4 mr-1" />
            Maybe
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className={`${event.user_rsvp === 'not_going' ? 'text-red-500' : 'text-[var(--ps-text-secondary)]'}`}
            onClick={() => onRSVP(event.event_id, 'not_going')}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

// Create Event Modal
const CreateEventModal = ({ isOpen, onClose, onEventCreated, user }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'casual',
    course_name: '',
    location: '',
    event_date: '',
    start_time: '',
    max_participants: '',
    entry_fee: '',
    is_private: false,
    cover_image: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [invitees, setInvitees] = useState([]);
  const [submitError, setSubmitError] = useState('');
  const submittingRef = useRef(false);
  const attempt = useRef(null);

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Compress image
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const MAX_WIDTH = 1200;
        
        if (width > MAX_WIDTH) {
          height = (height * MAX_WIDTH) / width;
          width = MAX_WIDTH;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setFormData(prev => ({ ...prev, cover_image: dataUrl }));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    if (!formData.title.trim()) {
      toast.error("Please enter an event title");
      return;
    }
    if (!formData.event_date) {
      toast.error("Please select an event date");
      return;
    }
    
    const payload = { ...formData, title: formData.title.trim(),
      invited_user_ids: invitees.map(m => m.user_id).sort(),
      max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
      entry_fee: formData.entry_fee ? parseFloat(formData.entry_fee) : 0 };
    const fingerprint = JSON.stringify(payload);
    if (!attempt.current || attempt.current.fingerprint !== fingerprint) attempt.current = { fingerprint, id: crypto.randomUUID() };
    submittingRef.current = true;
    setSubmitting(true); setSubmitError('');
    try {
      const response = await axios.post(`${API}/social-events`, {
        ...payload, request_id: attempt.current.id
      });
      toast.success("Event created!");
      onEventCreated?.(response.data);
      handleClose(true);
    } catch (error) {
      setSubmitError(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Could not create event. Please retry.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleClose = (created = false) => {
    if (submittingRef.current && created !== true) return;
    setInvitees([]); setSubmitError(''); attempt.current = null;
    setFormData({
      title: '',
      description: '',
      event_type: 'casual',
      course_name: '',
      location: '',
      event_date: '',
      start_time: '',
      max_participants: '',
      entry_fee: '',
      is_private: false,
      cover_image: null
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => { if (!open) handleClose(); }}>
      <DialogContent data-testid="create-event-dialog" className="ps-themed-dialog bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold" data-testid="create-event-title">Create Event</DialogTitle>
          <DialogDescription className="sr-only" data-testid="create-event-description-label">Event details and invited members</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Cover Image */}
          <div>
            <Label className="text-[var(--ps-text-secondary)] text-sm">Cover Image (optional)</Label>
            {formData.cover_image ? (
              <div className="relative mt-2 h-32 rounded-xl overflow-hidden">
                <img src={formData.cover_image} alt="Cover" className="w-full h-full object-cover" />
                <button
                  onClick={() => setFormData(prev => ({ ...prev, cover_image: null }))}
                  data-testid="create-event-remove-cover" aria-label="Remove cover"
                  className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="mt-2 flex items-center justify-center h-24 border-2 border-dashed border-[var(--ps-border)] rounded-xl cursor-pointer hover:border-[var(--ps-primary)] transition-colors">
                <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" data-testid="create-event-cover" />
                <div className="flex items-center gap-2 text-[var(--ps-text-muted)]">
                  <Camera className="w-5 h-5" />
                  <span>Add cover photo</span>
                </div>
              </label>
            )}
          </div>

          {/* Title */}
          <div>
            <Label className="text-[var(--ps-text-secondary)] text-sm">Event Title *</Label>
            <Input
              value={formData.title}
              data-testid="create-event-name" maxLength={160}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Saturday Morning Round"
              className="dark-input mt-1"
            />
          </div>

          {/* Event Type */}
          <div>
            <Label className="text-[var(--ps-text-secondary)] text-sm">Event Type</Label>
            <Select 
              value={formData.event_type} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, event_type: v }))}
            >
              <SelectTrigger className="dark-input mt-1" data-testid="create-event-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)]" data-testid="create-event-type-menu">
                {eventTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value} data-testid={`create-event-type-${type.value}`}>
                    <span className="flex items-center gap-2">
                      <span>{type.icon}</span>
                      <span>{type.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[var(--ps-text-secondary)] text-sm">Date *</Label>
              <Input
                type="date"
                value={formData.event_date}
                data-testid="create-event-date"
                onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                className="dark-input mt-1"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label className="text-[var(--ps-text-secondary)] text-sm">Start Time</Label>
              <Input
                type="time"
                value={formData.start_time}
                data-testid="create-event-time"
                onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                className="dark-input mt-1"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <Label className="text-[var(--ps-text-secondary)] text-sm">Golf Course</Label>
            <Input
              value={formData.course_name}
              data-testid="create-event-course"
              onChange={(e) => setFormData(prev => ({ ...prev, course_name: e.target.value }))}
              placeholder="Pebble Beach Golf Links"
              className="dark-input mt-1"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-[var(--ps-text-secondary)] text-sm">Description</Label>
            <Textarea
              value={formData.description}
              data-testid="create-event-description"
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Tell people what this event is about..."
              className="dark-input mt-1 min-h-[80px] resize-none"
            />
          </div>

          {/* Max Participants & Entry Fee */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[var(--ps-text-secondary)] text-sm">Max Players</Label>
              <Input
                type="number"
                value={formData.max_participants}
                data-testid="create-event-max-players"
                onChange={(e) => setFormData(prev => ({ ...prev, max_participants: e.target.value }))}
                placeholder="No limit"
                className="dark-input mt-1"
                min="2"
              />
            </div>
            <div>
              <Label className="text-[var(--ps-text-secondary)] text-sm">Entry Fee ($)</Label>
              <Input
                type="number"
                value={formData.entry_fee}
                data-testid="create-event-entry-fee"
                onChange={(e) => setFormData(prev => ({ ...prev, entry_fee: e.target.value }))}
                placeholder="0"
                className="dark-input mt-1"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Private Event Toggle */}
          <div className="flex items-center justify-between p-3 bg-[var(--ps-bg-base)] rounded-xl">
            <div className="flex items-center gap-3">
              {formData.is_private ? (
                <Lock className="w-5 h-5 text-[var(--ps-primary)]" />
              ) : (
                <Globe className="w-5 h-5 text-[var(--ps-text-muted)]" />
              )}
              <div>
                <p className="font-medium text-sm">Private Event</p>
                <p className="text-xs text-[var(--ps-text-muted)]">
                  {formData.is_private ? 'Invite only' : 'Anyone can join'}
                </p>
              </div>
            </div>
            <Switch
              checked={formData.is_private}
              data-testid="create-event-private"
              onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_private: v }))}
            />
          </div>
          {isOpen && <EventMemberPicker selected={invitees} onChange={setInvitees} excludeIds={[user?.user_id]} disabled={submitting} />}
          {submitError && <p role="alert" className="text-sm text-red-500" data-testid="create-event-error">{submitError}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleClose()} disabled={submitting} className="text-[var(--ps-text-secondary)]" data-testid="create-event-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} className="dark-btn-primary" data-testid="create-event-submit">
            {submitting ? <div className="dark-spinner w-4 h-4" /> : 'Create Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Events Page Component
export const EventsPage = ({ user }) => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('upcoming');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [shareEvent, setShareEvent] = useState(null);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/social-events`, {
        params: { filter: activeFilter }
      });
      setEvents(response.data);
    } catch (error) {
      console.error("Failed to fetch events:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // RSVP to event
  const handleRSVP = async (eventId, status) => {
    try {
      await axios.post(`${API}/social-events/${eventId}/rsvp`, { status });
      toast.success(status === 'going' ? "You're going!" : status === 'maybe' ? "Marked as maybe" : "RSVP updated");
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to RSVP");
    }
  };

  // View event details
  const handleViewEvent = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  // Handle event created
  const handleEventCreated = (newEvent) => {
    setEvents(previous => [newEvent, ...previous.filter(e => e.event_id !== newEvent.event_id)]);
    setShareEvent(newEvent);
  };

  return (
    <div className="pb-4" data-testid="events-page">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--ps-border)]">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold text-[var(--ps-text-primary)]">Events</h1>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="text-[var(--ps-primary)] font-semibold text-base hover:underline transition-colors"
            data-testid="create-event-btn"
          >
            Create
          </button>
        </div>

        {/* Filter Tabs - Text with underline style */}
        <div className="flex items-center gap-4">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={`pb-2 text-lg font-semibold transition-colors border-b-2 ${
                activeFilter === tab.id
                  ? 'text-[var(--ps-primary)] border-[var(--ps-primary)]'
                  : 'text-[var(--ps-text-secondary)] border-transparent hover:text-[var(--ps-text-primary)]'
              }`}
              data-testid={`filter-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Events List */}
      <div className="p-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="dark-spinner" />
          </div>
        ) : events.length === 0 ? (
          <div className="dark-empty-state">
            <Calendar className="w-12 h-12 mb-4" />
            <h3 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-2">
              {activeFilter === 'upcoming' ? 'No upcoming events' :
               activeFilter === 'my_events' ? 'No events yet' : 'No past events'}
            </h3>
            <p className="text-[var(--ps-text-secondary)] mb-4">
              {activeFilter === 'upcoming' 
                ? 'Be the first to organize a golf event!'
                : 'Events you create or join will appear here.'}
            </p>
            {activeFilter !== 'past' && (
              <button 
                className="text-[var(--ps-primary)] font-semibold text-base hover:underline transition-colors"
                onClick={() => setShowCreateModal(true)}
              >
                Create Event
              </button>
            )}
          </div>
        ) : (
          events.map((event) => (
            <EventCard
              key={event.event_id}
              event={event}
              onRSVP={handleRSVP}
              onView={handleViewEvent}
              currentUserId={user?.user_id}
            />
          ))
        )}
      </div>

      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onEventCreated={handleEventCreated}
        user={user}
      />
      {shareEvent && <EventShareDialog event={shareEvent} isOrganizer onClose={() => setShareEvent(null)} />}
    </div>
  );
};

export default EventsPage;
